import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { api } from "../../axios/api";
import { numberFormat } from "../../config/Config";
import Swal from "sweetalert2";
import { ArrowLeft } from "lucide-react";
import FaturaSucessoModal from "../../components/FaturaSucesso";

const EmitirReciboGeral = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
  const empresaId = user?.empresa_id;

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [valorRecebido, setValorRecebido] = useState(0);
  const [metodoPagamento, setMetodoPagamento] = useState("Dinheiro"); 
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [faturaGeradaId, setFaturaGeradaId] = useState(null);

  const texts = {
    "pt-AO": {
      title: "Emissão de Recibo Geral (RG)",
      subtitle: "A liquidar documento",
      sectionInfo: "Informações do Documento de Origem",
      labelClient: "CLIENTE",
      total: "TOTAL",
      balance: "SALDO EM ABERTO",
      labelMethod: "Meio de Pagamento",
      labelAmount: "Valor a Liquidar",
      btnConfirm: "EMITIR E ASSINAR RG"
    }
  };

  const t = texts[language] || texts["pt-AO"];

  useEffect(() => {
    if (id && empresaId) fetchDadosIniciais();
  }, [id, empresaId]);

  const fetchDadosIniciais = async () => {
    try {
      setLoading(true);
      const resDoc = await api.get(`/app/documentos/${id}`);
      const data = resDoc.data;
      const pendente = data.total_geral - (data.valor_pago || 0);

      if (pendente <= 0.01 && data.estado === "Liquidado") {
        Swal.fire("Atenção", "Documento já liquidado.", "info");
        navigate(-1);
        return;
      }
      setDoc(data);
      setValorRecebido(Number(pendente.toFixed(2)));
    } catch (error) {
      Swal.fire("Erro", "Falha ao carregar dados.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBlurValor = () => {
    setValorRecebido(prev => Number(parseFloat(prev).toFixed(2)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valorNum = parseFloat(valorRecebido);
    const saldoPendente = doc.total_geral - (doc.valor_pago || 0);

    if (valorNum <= 0) return Swal.fire("Aviso", "Valor inválido.", "warning");
    if (valorNum > (saldoPendente + 0.01)) return Swal.fire("Aviso", "Valor superior ao saldo.", "warning");

    try {
      setIsProcessing(true);
      const payload = {
        doc_type: "RG",
        numero: "PENDENTE", // O backend deve sobrescrever com a numeração correta
        numero_fatura_origem: doc.numero,
        cliente_id: doc.cliente_id,
        empresa_id: empresaId,
        user_id: user.id,
        data_emissao: new Date().toISOString().split('T')[0],
        data_vencimento: new Date().toISOString().split('T')[0],
        total_liquido: valorNum,
        total_desconto: 0.00,
        total_iva: 0.00,
        total_geral: valorNum,
        retencao_fonte: 0.00,
        total_retido: 0.00,
        metodo_pagamento: metodoPagamento,
        valor_pago: valorNum,
        troco: 0.00,
        estado: "Emitido",
        documentos_pagos: [doc.id] // Array para a tabela de associação
      };

      const res = await api.post(`/app/documentos/create/recibo-geral`, payload);
      setFaturaGeradaId(res.data.id);
      setShowSuccessModal(true);
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Erro na emissão.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading || !doc) return <div className="p-5 text-center"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f3f2f1', padding: '20px' }}>
      <div className="container-fluid py-4">
        <div className="bg-white p-3 mb-4 shadow-sm">
           <button className="btn btn-light border" onClick={() => navigate(-1)}><ArrowLeft size={18} /></button>
           <h4 className="fw-bold mt-2">{t.title}</h4>
        </div>
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="bg-white p-4 shadow-sm">
              <h5>{t.sectionInfo}</h5>
              <p><strong>{t.labelClient}:</strong> {doc.Cliente?.nome}</p>
              <div className="d-flex justify-content-between p-2 bg-light">
                 <span>{t.total}: {numberFormat(doc.total_geral)} Kz</span>
                 <span className="text-danger">{t.balance}: {numberFormat(doc.total_geral - (doc.valor_pago || 0))} Kz</span>
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <form onSubmit={handleSubmit} className="bg-white p-4 shadow-sm">
              <label className="fw-bold">{t.labelMethod}</label>
              <select className="form-select mb-3" value={metodoPagamento} onChange={(e) => setMetodoPagamento(e.target.value)}>
                <option value="Dinheiro">Dinheiro</option>
                <option value="Transferência">Transferência</option>
                <option value="TPA">TPA</option>
                <option value="Multicaixa">Multicaixa</option>
              </select>
              <label className="fw-bold">{t.labelAmount}</label>
              <input type="number" className="form-control mb-3" value={valorRecebido} onChange={(e) => setValorRecebido(e.target.value)} onBlur={handleBlurValor} required />
              <button type="submit" className="btn btn-primary w-100" disabled={isProcessing}>{isProcessing ? "Processando..." : t.btnConfirm}</button>
            </form>
          </div>
        </div>
      </div>
      {showSuccessModal && <FaturaSucessoModal faturaId={faturaGeradaId} onClose={() => navigate("/faturacao/documentos/emitidos/")} />}
    </div>
  );
};

export default EmitirReciboGeral;